import { create } from 'zustand';
import { persist, createJSONStorage, devtools } from 'zustand/middleware';
import { MRT_ColumnFiltersState, MRT_SortingState } from 'mantine-react-table';
import { scrubDateFilter } from 'utils/filterUtils';
import { toJSDate } from 'utils/dateUtils';

export interface Pagination {
  pageIndex: number;
  pageSize: number;
  globalFilter: string; // We default to an empty string instead of undefined
  columnFilters: MRT_ColumnFiltersState;
  sorting: MRT_SortingState;
}

interface ClaimPaginationState {
  // The entire pagination object is our single source of truth
  pagination: Pagination;
  // A setter that replaces the entire pagination object
  setPagination: (
    pagination: Pagination | ((prev: Pagination) => Pagination)
  ) => void;
  setGlobalFilter: (globalFilter: string | undefined) => void;
  setColumnFilters: (columnFilters: MRT_ColumnFiltersState) => void;
  setSorting: (sorting: MRT_SortingState) => void;
  setPageIndex: (pageIndex: number) => void;
  resetPagination: () => void;
}

export const DEFAULT_CLAIMS_PAGE_SIZE = 50;

const dateReviver = (_key: string, value: any) => {
  if (
    typeof value === 'string' &&
    /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/.test(value)
  ) {
    return toJSDate(value);
  }
  return value;
};

const normalizePagination = (pagination: Pagination): Pagination => ({
  ...pagination,
  globalFilter: pagination.globalFilter ?? '',
  columnFilters: scrubDateFilter(pagination.columnFilters),
  sorting: pagination.sorting ?? [],
});

const isPaginationEqual = (left: Pagination, right: Pagination) => {
  const normalizedLeft = normalizePagination(left);
  const normalizedRight = normalizePagination(right);
  return (
    normalizedLeft.pageIndex === normalizedRight.pageIndex &&
    normalizedLeft.pageSize === normalizedRight.pageSize &&
    normalizedLeft.globalFilter === normalizedRight.globalFilter &&
    JSON.stringify(normalizedLeft.columnFilters) ===
      JSON.stringify(normalizedRight.columnFilters) &&
    JSON.stringify(normalizedLeft.sorting) ===
      JSON.stringify(normalizedRight.sorting)
  );
};

export const useClaimPaginationStore = create<ClaimPaginationState>()(
  devtools(
    persist(
      (set) => ({
        pagination: {
          pageIndex: 0,
          pageSize: DEFAULT_CLAIMS_PAGE_SIZE,
          globalFilter: '',
          columnFilters: [] as MRT_ColumnFiltersState,
          sorting: [] as MRT_SortingState,
        },
        setPagination: (upd) =>
          set((state) => {
            const next =
              typeof upd === 'function' ? upd(state.pagination) : upd;
            if (isPaginationEqual(state.pagination, next)) {
              return state;
            }
            return {
              pagination: normalizePagination(next),
            };
          }),

        resetPagination: () =>
          set({
            pagination: {
              pageIndex: 0,
              pageSize: DEFAULT_CLAIMS_PAGE_SIZE,
              globalFilter: '',
              columnFilters: [],
              sorting: [],
            },
          }),
        setGlobalFilter: (globalFilter) =>
          set((state) => ({
            pagination:
              state.pagination.globalFilter === (globalFilter ?? '')
                ? state.pagination
                : { ...state.pagination, globalFilter: globalFilter ?? '' },
          })),
        setColumnFilters: (columnFilters) => {
          const clean = scrubDateFilter(columnFilters);
          set((state) =>
            JSON.stringify(state.pagination.columnFilters) ===
            JSON.stringify(clean)
              ? state
              : {
                  pagination: { ...state.pagination, columnFilters: clean },
                }
          );
        },
        setSorting: (sorting) =>
          set((state) =>
            JSON.stringify(state.pagination.sorting) === JSON.stringify(sorting)
              ? state
              : { pagination: { ...state.pagination, sorting } }
          ),
        setPageIndex: (pageIndex) =>
          set((state) =>
            state.pagination.pageIndex === pageIndex
              ? state
              : { pagination: { ...state.pagination, pageIndex } }
          ),
      }),

      {
        name: 'claim-pagination-storage',
        storage: createJSONStorage(() => localStorage),
        deserialize: (str: string) => JSON.parse(str, dateReviver),
      }
    ),
    {
      name: 'ClaimPaginationStore',
      store: 'ClaimPaginationStore',
      anonymousActionType: 'action',
      enabled: true,
    }
  )
);
